import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { message, Upload } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/lib/upload';

import type { TPhotoValue } from '@src/models/interfaces/components/photoComponent.interface';
import { getPhotoSizeInKilobytes } from '@src/services/WorkOrderPhotoService';

import type { PhotoInputValue, UsePhotoUploadInputParams } from '../PhotoUploadInput.types';

const MAX_COUNT_MESSAGE_KEY = 'dynamic-form-photo-max-count';

const usePhotoUploadInput = ({
  divisionId,
  maxCount,
  maxSize,
  onChange,
  organizationId,
  photoService,
  value,
}: UsePhotoUploadInputParams) => {
  const intl = useIntl();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const valuesRef = useRef<PhotoInputValue[]>(value ?? []);
  const photosByFileIdRef = useRef(new Map<string, TPhotoValue>());
  // antd runs `beforeUpload` for every file in a batch before any upload starts, so slots
  // accepted in the batch are reserved here until their placeholder lands in the value.
  const reservedSlotsRef = useRef(0);

  useEffect(() => {
    valuesRef.current = value ?? [];
  }, [value]);

  const updateValues = useCallback(
    (nextValue: PhotoInputValue[]) => {
      valuesRef.current = nextValue;
      onChange?.(nextValue);
    },
    [onChange],
  );

  const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    (file: RcFile) => {
      if (!file.type.startsWith('image/')) {
        message.error(intl.formatMessage({ id: 'general.upload.allowedFileType' }));
        return Upload.LIST_IGNORE;
      }

      if (maxSize && getPhotoSizeInKilobytes(file.size) > maxSize) {
        message.error(
          intl.formatMessage({ id: 'dynamic.form.photo.maxSize.message' }, { maxSize }),
        );
        return Upload.LIST_IGNORE;
      }

      if (maxCount && valuesRef.current.length + reservedSlotsRef.current >= maxCount) {
        message.error({
          content: intl.formatMessage({ id: 'dynamic.form.photo.maxCount.message' }, { maxCount }),
          key: MAX_COUNT_MESSAGE_KEY,
        });
        return Upload.LIST_IGNORE;
      }

      reservedSlotsRef.current += 1;
      return true;
    },
    [intl, maxCount, maxSize],
  );

  const uploadPhoto = useCallback<NonNullable<UploadProps['customRequest']>>(
    async ({ file, onError, onSuccess }) => {
      const rcFile = file as RcFile;
      reservedSlotsRef.current = Math.max(reservedSlotsRef.current - 1, 0);
      updateValues([
        ...valuesRef.current,
        {
          size: getPhotoSizeInKilobytes(rcFile.size),
          tags: [],
          uid: rcFile.uid,
          uploading: true,
          url: '',
        },
      ]);

      try {
        const photo = await photoService.upload({ divisionId, file: rcFile, organizationId });
        photosByFileIdRef.current.set(rcFile.uid, photo);
        updateValues(
          valuesRef.current.map(current => (current.uid === rcFile.uid ? photo : current)),
        );
        onSuccess?.(photo);
      } catch (error) {
        updateValues(valuesRef.current.filter(current => current.uid !== rcFile.uid));
        onError?.(error as Error);
        message.error(intl.formatMessage({ id: 'dynamic.form.photo.upload.error' }));
      }
    },
    [divisionId, intl, organizationId, photoService, updateValues],
  );

  const removePhoto = useCallback(
    async (file: UploadFile) => {
      if (file.status === 'uploading') {
        return false;
      }

      const photo = photosByFileIdRef.current.get(file.uid);
      if (!photo) {
        return true;
      }

      try {
        await photoService.remove(photo);
        photosByFileIdRef.current.delete(file.uid);
        updateValues(valuesRef.current.filter(current => current.url !== photo.url));
        return true;
      } catch {
        message.error(intl.formatMessage({ id: 'dynamic.form.photo.remove.error' }));
        return false;
      }
    },
    [intl, photoService, updateValues],
  );

  const onFileListChange = useCallback<NonNullable<UploadProps['onChange']>>(
    ({ fileList: nextFileList }) => setFileList(nextFileList),
    [],
  );

  return { beforeUpload, fileList, onFileListChange, removePhoto, uploadPhoto };
};

export default usePhotoUploadInput;
